import { FormEvent, useState } from 'react';
import { request } from '../lib/api';
import { totpError } from '../lib/authSecurity';
import { SecurityMethodIcon } from './SecurityMethodIcon';

type TotpSetup = { challengeId: string; secret: string; otpauthUrl: string };
export function CustomerTotpSettings({ enabled, configured = true, onSignedOut }: { enabled: boolean; configured?: boolean; onSignedOut: () => void }) {
  const [setup, setSetup] = useState<TotpSetup | null>(null);
  const [code, setCode] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const begin = async () => {
    setPending(true); setError(null);
    try { setSetup(await request<TotpSetup>('/auth/totp/enroll/options', { method: 'POST', body: '{}' })); }
    catch (err) { setError(totpError(err)); }
    finally { setPending(false); }
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault(); setPending(true); setError(null);
    try {
      if (enabled) {
        if (!window.confirm('Turn off authenticator MFA? Your email and Google sign-in will no longer require an authenticator code.')) return;
        await request('/auth/totp', { method: 'DELETE', body: JSON.stringify({ code: code.trim() }) });
      } else if (setup) {
        await request('/auth/totp/enroll/verify', { method: 'POST', body: JSON.stringify({ challengeId: setup.challengeId, code: code.trim() }) });
      } else return;
      setCode(''); setSetup(null); onSignedOut();
    } catch (err) { setError(totpError(err)); setCode(''); }
    finally { setPending(false); }
  };
  if (!configured && !enabled) return <div className="form-stack" aria-label="Authenticator MFA"><div className="security-method-heading"><SecurityMethodIcon method="shield" /><div><h3>Authenticator MFA</h3></div></div><p className="helper-text">Authenticator MFA is unavailable. You can secure your account with a registered key or passkey.</p></div>;
  return <div className="form-stack" aria-label="Authenticator MFA">
    <div className="security-method-heading"><SecurityMethodIcon method="shield" /><div><h3>Authenticator MFA</h3></div></div>
    <p className="helper-text">Without a registered key or passkey, you can add an optional authenticator code after email or Google sign-in. Keys and passkeys use their own required verification and cannot be bypassed with an authenticator code.</p>
    {error ? <div className="alert" role="alert">{error}</div> : null}
    {enabled ? <p>Authenticator MFA is on.</p> : <button className="button-secondary" type="button" onClick={begin} disabled={pending || !!setup}>{pending ? 'Preparing...' : 'Set up authenticator MFA'}</button>}
    {setup ? <div className="alert" role="note">
      <p>Add this account to your authenticator app, then enter its current six-digit code. Keep the setup key private.</p>
      <a className="button-secondary" href={setup.otpauthUrl}>Open in authenticator app</a>
      <p>Manual setup key: <code style={{ overflowWrap: 'anywhere' }}>{setup.secret}</code></p>
    </div> : null}
    {enabled || setup ? <form className="form-stack" onSubmit={submit}>
      <label>Authenticator code<input type="text" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required value={code} onChange={event => setCode(event.target.value)} disabled={pending} /></label>
      <button className="button-primary" type="submit" disabled={pending}>{pending ? 'Verifying...' : enabled ? 'Turn off authenticator MFA' : 'Enable authenticator MFA'}</button>
      {setup ? <button className="button-secondary" type="button" disabled={pending} onClick={() => { setSetup(null); setCode(''); setError(null); }}>Cancel setup</button> : null}
    </form> : null}
  </div>;
}
